
import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, LayoutGrid, Clock, Users, Calendar } from 'lucide-react';
import { api, ShiftPattern, Store, Staff, Position } from '../../api';
import Modal from '../ui/Modal';
import Dialog from '../ui/Dialog';
import Input from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';

interface ShiftTemplateSettingsTabProps {
  stores: Store[];
  myProfile: Staff | null;
  isSystemAdmin: boolean;
}

const ShiftTemplateSettingsTab: React.FC<ShiftTemplateSettingsTabProps> = ({ stores: allStores, myProfile, isSystemAdmin }) => {
  const { showToast } = useToast();
  const [patterns, setPatterns] = useState<ShiftPattern[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [dialog, setDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [editing, setEditing] = useState<ShiftPattern | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const availableStores = allStores.filter(s => isSystemAdmin || myProfile?.stores?.includes(s.id));
  const DAYS = ['日', '月', '火', '水', '木', '金', '土'];

  // 初回表示時に対象店舗の初期値を設定
  useEffect(() => {
    if (availableStores.length > 0 && !selectedStoreId) {
        setSelectedStoreId(availableStores[0].id);
    }
  }, [availableStores, selectedStoreId]);

  const fetchData = async (storeId: string) => {
    if (!storeId) return;
    setIsLoading(true);
    try {
      const [pat, pos] = await Promise.all([
          api.getShiftPatterns(storeId),
          api.getPositions()
      ]);
      setPatterns(pat || []);
      setPositions(pos || []);
    } catch (e) {
      console.error(e);
      showToast('データの取得に失敗しました。', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // selectedStoreId が変更された時のみデータを取得
  useEffect(() => {
    if (selectedStoreId) {
        fetchData(selectedStoreId);
    }
  }, [selectedStoreId]);

  const handleUpdateDays = async (pattern: ShiftPattern, day: number) => {
      const currentDays = pattern.defaultDays || [];
      const newDays = currentDays.includes(day) ? currentDays.filter(d => d !== day) : [...currentDays, day];
      const updated = { ...pattern, defaultDays: newDays };
      // 1曜日につき1モデル：他のモデルシフトに同じ曜日があれば外す。
      const others = currentDays.includes(day) ? [] : patterns.filter(p => p.id !== pattern.id && (p.defaultDays || []).includes(day)).map(p => ({ ...p, defaultDays: (p.defaultDays || []).filter(d => d !== day) }));

      try {
          await Promise.all([updated, ...others].map(p => api.saveShiftPattern(p)));
          setPatterns(prev => prev.map(p => p.id === pattern.id ? updated : (others.find(o => o.id === p.id) || p)));
          showToast('曜日の設定を更新しました。');
      } catch (e) {
          showToast('保存に失敗しました。', 'error');
      }
  };

  const storePositions = positions.filter(p => p.storeIds.includes(selectedStoreId));

  const openNew = () => {
    const first = storePositions[0]?.id || '';
    setEditing({ id: `pattern_${Date.now()}`, storeId: selectedStoreId, name: '', defaultDays: [], items: [{ positionId: first, startTime: '10:00', endTime: '18:00', headcount: 1 }] });
  };

  const updateItem = (idx: number, patch: Partial<ShiftPattern['items'][number]>) => {
    if (!editing) return;
    setEditing({ ...editing, items: editing.items.map((it, i) => i === idx ? { ...it, ...patch } : it) });
  };

  const handleSaveEditing = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { showToast('モデルシフト名を入力してください。', 'error'); return; }
    if (editing.items.length === 0) { showToast('募集枠を1つ以上追加してください。', 'error'); return; }
    if (editing.items.some(it => !it.positionId || !it.startTime || !it.endTime || it.startTime === it.endTime || it.headcount < 1)) {
      showToast('ポジション・時間・人数を正しく入力してください。', 'error'); return;
    }
    const positionIds = editing.items.map(it => it.positionId);
    if (new Set(positionIds).size !== positionIds.length) {
      showToast('1つのポジションにつき募集枠は1つまでです。時間帯ごとにポジションを分けてください。', 'error'); return;
    }
    setIsSaving(true);
    try {
      // 同じ曜日が他のモデルシフトに設定されている場合は、そちらから外す（1曜日につき1モデル）。
      const days = editing.defaultDays || [];
      const others = patterns.filter(p => p.id !== editing.id && (p.defaultDays || []).some(d => days.includes(d)))
        .map(p => ({ ...p, defaultDays: (p.defaultDays || []).filter(d => !days.includes(d)) }));
      await Promise.all([...others, editing].map(p => api.saveShiftPattern(p)));
      setPatterns(prev => {
        const map = new Map(prev.map(p => [p.id, p]));
        [...others, editing].forEach(p => map.set(p.id, p));
        return Array.from(map.values());
      });
      setEditing(null);
      showToast('モデルシフトを保存しました。', 'success');
    } catch (e) {
      showToast('保存に失敗しました。', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDialog({
      isOpen: true,
      title: 'モデルシフトの削除',
      message: 'このモデルシフトを削除しますか？',
      onConfirm: async () => {
        try {
          await api.deleteShiftPattern(id);
          setPatterns(prev => prev.filter(p => p.id !== id));
          showToast('削除しました。', 'success');
        } catch (e) {
          showToast('削除に失敗しました。', 'error');
        }
        setDialog(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Dialog {...dialog} type="confirm" isDestructive onCancel={() => setDialog(prev => ({ ...prev, isOpen: false }))} />
      
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-bold text-slate-800">モデルシフト一覧</h2>
          <select value={selectedStoreId} onChange={(e) => setSelectedStoreId(e.target.value)} className="bg-white border border-gray-300 rounded-[4px] px-3 py-2 text-sm font-bold text-slate-600 outline-none">
            {availableStores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button onClick={openNew} disabled={!selectedStoreId} className="bg-blue-600 text-white font-bold px-4 py-2 rounded text-sm flex items-center gap-1.5 shadow-md hover:bg-blue-700 whitespace-nowrap"><Plus size={16} />モデルシフトを作成</button>
      </div>

      <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex items-start gap-3">
          <Calendar className="text-blue-500 mt-1" size={20} />
          <div className="text-xs text-blue-800 leading-relaxed font-bold">
              曜日ごとに、必要なポジション・時間帯・人数のモデルを設定できます。<br/>
              シフト作成で期間を指定すると、各曜日のモデルシフトから募集枠が自動で作成されます。
          </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
              <div className="col-span-full py-20 flex justify-center"><Loader2 className="animate-spin text-slate-300" size={40} /></div>
          ) : patterns.length === 0 ? (
              <div className="col-span-full py-20 text-center bg-white border border-dashed rounded-xl border-slate-300 text-slate-400 font-bold">
                  モデルシフトはまだありません。
              </div>
          ) : patterns.map(pattern => (
              <div key={pattern.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group relative">
                  <div className="flex justify-between items-start mb-4">
                      <h3 className="font-bold text-slate-800 text-lg">{pattern.name}</h3>
                      <div className="flex items-center gap-1">
                          <button onClick={() => setEditing({ ...pattern, items: pattern.items.map(it => ({ ...it })) })} aria-label="編集" className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"><Edit size={18} /></button>
                          <button onClick={() => handleDelete(pattern.id)} aria-label="削除" className="p-1.5 text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                      </div>
                  </div>
                  
                  <div className="mb-4 flex flex-wrap gap-1">
                      {DAYS.map((day, idx) => {
                          const isActive = pattern.defaultDays?.includes(idx);
                          return (
                              <button
                                  key={day}
                                  onClick={() => handleUpdateDays(pattern, idx)}
                                  className={`w-7 h-7 rounded-full text-[10px] font-black border transition-all ${isActive ? 'bg-blue-600 border-blue-600 text-white shadow-sm' : 'bg-white text-slate-300 border-slate-200 hover:border-slate-300'}`}
                              >
                                  {day}
                              </button>
                          );
                      })}
                  </div>
                  
                  <div className="space-y-2 mb-4">
                      {pattern.items.map((item, idx) => {
                          const pos = positions.find(p => p.id === item.positionId);
                          return (
                              <div key={idx} className="flex items-center justify-between text-[11px] font-bold text-slate-500 bg-slate-50 px-2 py-1.5 rounded">
                                  <div className="flex items-center gap-1.5">
                                      <div className={`w-1.5 h-1.5 rounded-full ${pos?.color.split(' ')[0] || 'bg-gray-400'}`} />
                                      <span>{pos?.name || '不明'}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                      <span className="flex items-center tabular-nums"><Clock size={12} className="mr-1" />{item.startTime}〜{item.endTime <= item.startTime ? '翌' : ''}{item.endTime}</span>
                                      <span className="flex items-center tabular-nums"><Users size={12} className="mr-1" />{item.headcount}名</span>
                                  </div>
                              </div>
                          );
                      })}
                      <div className="text-[11px] text-slate-500 font-bold text-right pt-1">合計 {pattern.items.reduce((a, it) => a + (it.headcount || 0), 0)}名</div>
                  </div>
              </div>
          ))}
      </div>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={editing && patterns.some(p => p.id === editing.id) ? 'モデルシフトの編集' : 'モデルシフトの作成'} maxWidth="max-w-2xl">
        {editing && (
          <div className="space-y-6 pt-2">
            <Input label="モデルシフト名" value={editing.name} placeholder="例：平日（通常）、土日祝（繁忙）" onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <div>
              <label className="text-xs font-bold text-slate-500 mb-2 block">適用する曜日</label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((day, idx) => {
                  const active = editing.defaultDays?.includes(idx);
                  const usedBy = patterns.find(p => p.id !== editing.id && p.defaultDays?.includes(idx));
                  return (
                    <button key={day} type="button" title={usedBy ? `現在は「${usedBy.name}」に設定されています。` : ''}
                      onClick={() => setEditing({ ...editing, defaultDays: active ? (editing.defaultDays || []).filter(d => d !== idx) : [...(editing.defaultDays || []), idx] })}
                      className={`w-10 h-10 rounded-full text-xs font-black border transition-all ${active ? 'bg-blue-600 border-blue-600 text-white' : usedBy ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-white text-slate-500 border-slate-300 hover:border-slate-400'}`}>
                      {day}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 font-bold mt-2">他のモデルシフトに設定済みの曜日を選ぶと、こちらに付け替えます。</p>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 mb-2 block">募集枠（ポジション・時間帯・必要人数）</label>
              <div className="space-y-2">
                {editing.items.map((it, idx) => (
                  <div key={idx} className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
                    <select value={it.positionId} onChange={(e) => updateItem(idx, { positionId: e.target.value })} className="flex-1 min-w-[140px] border border-slate-300 rounded px-2 py-1.5 text-sm font-bold bg-white">
                      {storePositions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input type="time" value={it.startTime} onChange={(e) => updateItem(idx, { startTime: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white" aria-label="開始時刻" />
                    <span className="text-slate-400">〜</span>
                    {it.endTime && it.startTime && it.endTime <= it.startTime && <span className="text-xs font-bold text-indigo-700 whitespace-nowrap">翌</span>}
                    <input type="time" value={it.endTime} onChange={(e) => updateItem(idx, { endTime: e.target.value })} className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white" aria-label="終了時刻" />
                    <div className="flex items-center gap-1">
                      <input type="number" min={1} value={it.headcount} onChange={(e) => updateItem(idx, { headcount: Math.max(1, Math.round(Number(e.target.value) || 1)) })} className="w-16 border border-slate-300 rounded px-2 py-1.5 text-sm font-bold bg-white" aria-label="必要人数" />
                      <span className="text-xs font-bold text-slate-500">名</span>
                    </div>
                    <button type="button" onClick={() => setEditing({ ...editing, items: editing.items.filter((_, i) => i !== idx) })} aria-label="この枠を削除" className="p-1.5 text-slate-300 hover:text-red-500"><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setEditing({ ...editing, items: [...editing.items, { positionId: storePositions.find(p => !editing.items.some(it => it.positionId === p.id))?.id || storePositions[0]?.id || '', startTime: '10:00', endTime: '18:00', headcount: 1 }] })} className="mt-2 text-sm font-bold text-blue-600 flex items-center gap-1 hover:underline"><Plus size={14} />募集枠を追加</button>
            </div>
            <div className="pt-4 flex justify-end gap-3 border-t">
              <button onClick={() => setEditing(null)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded">キャンセル</button>
              <button onClick={handleSaveEditing} disabled={isSaving} className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded shadow-md hover:bg-blue-700 flex items-center">{isSaving && <Loader2 className="animate-spin mr-2" size={16} />}保存</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ShiftTemplateSettingsTab;
